/**
 * dataResolution.ts — Single source of truth for data fallback and quality rules.
 *
 * RESPONSIBILITY: Decide which data source wins when multiple exist.
 * translate.ts receives already-resolved data and NEVER makes fallback decisions.
 *
 * Design principles:
 * 1. Every threshold is a named, exported constant — no magic numbers.
 * 2. Every resolution function is pure and testable.
 * 3. resolveIntelData() is the single entry point called by buildIntelMap().
 */

import type { IntelData } from "./translate";

// ── Named thresholds ──

/** Kronos 4H confidence below this → use hourly instead */
export const KRONOS_4H_MIN_CONFIDENCE = 0.3;

/**
 * If Kronos' first projected open is this far from the live input anchor,
 * the target ladder is disconnected from the realtime tape. Do not use that
 * projection for dashboard direction/summary; show it only with a warning.
 */
export const KRONOS_MAX_OPEN_ANCHOR_GAP_PCT = 0.005;

/** Momentum absolute value below this is noise, not directional signal */
export const MOMENTUM_MIN_ABSOLUTE = 0.5;

/** VIX at or above this → high fear */
export const VIX_HIGH_THRESHOLD = 30;

/** VIX at or above this → elevated fear */
export const VIX_ELEVATED_THRESHOLD = 25;

/** VIX at or below this → calm */
export const VIX_CALM_THRESHOLD = 14;

// ── Resolved type ──

/** IntelData after resolution. Translation layer reads these fields only. */
export interface ResolvedIntel extends IntelData {
  /** Session forecast direction after Kronos 4H/Hourly resolution */
  _session_forecast_dir?: string;
  /** Session forecast confidence after resolution */
  _session_forecast_conf?: number;
  /** Session forecast source label for display */
  _session_forecast_source?: "4H" | "Hourly" | "none";
  /** Session forecast price target/range after 4H/Hourly resolution */
  _session_forecast_close?: number;
  _session_forecast_high?: number;
  _session_forecast_low?: number;
  _session_forecast_range?: number;
  /** Whether momentum was neutralized (below threshold) */
  _momentum_neutralized?: boolean;
}

// ── Resolution functions (each pure, testable) ──

/**
 * Resolve session forecast: Kronos 4H vs Hourly.
 *
 * Priority:
 * 1. Kronos 4H valid, direction present, and confidence >= KRONOS_4H_MIN_CONFIDENCE → use 4H
 * 2. Kronos Hourly valid and direction present → use Hourly
 * 3. No forecast available
 */
export function resolveSessionForecast(data: IntelData): {
  direction: string | undefined;
  confidence: number | undefined;
  source: "4H" | "Hourly" | "none";
  close: number | undefined;
  high: number | undefined;
  low: number | undefined;
  range: number | undefined;
} {
  const k4hDir = data.kronos_4h_direction;
  const k4hConf = data.kronos_4h_confidence ?? 0;
  const khDir = data.kronos_hourly_direction;
  const khConf = data.kronos_hourly_confidence;
  const k4hDisconnected = isKronosProjectionDisconnected(
    data.kronos_4h_predicted_open,
    data.kronos_4h_input_anchor_price,
  );
  const khDisconnected = isKronosProjectionDisconnected(
    data.kronos_hourly_predicted_open,
    data.kronos_hourly_input_anchor_price,
  );
  const k4hUsable = data.kronos_4h_valid !== false && !k4hDisconnected;
  const khUsable = data.kronos_hourly_valid !== false && !khDisconnected;

  if (k4hDir && k4hConf >= KRONOS_4H_MIN_CONFIDENCE && k4hUsable) {
    return {
      direction: k4hDir,
      confidence: k4hConf,
      source: "4H",
      close: data.kronos_4h_predicted_close,
      high: data.kronos_4h_predicted_high,
      low: data.kronos_4h_predicted_low,
      range: data.kronos_4h_range,
    };
  }

  if (khDir && khUsable) {
    return {
      direction: khDir,
      confidence: khConf,
      source: "Hourly",
      close: data.kronos_hourly_predicted_close,
      high: data.kronos_hourly_predicted_high,
      low: data.kronos_hourly_predicted_low,
      range: data.kronos_hourly_range,
    };
  }

  return { direction: undefined, confidence: undefined, source: "none", close: undefined, high: undefined, low: undefined, range: undefined };
}

export function isKronosProjectionDisconnected(
  predictedOpen: number | undefined | null,
  inputAnchorPrice: number | undefined | null,
): boolean {
  if (
    predictedOpen === undefined || predictedOpen === null || !Number.isFinite(predictedOpen) ||
    inputAnchorPrice === undefined || inputAnchorPrice === null || !Number.isFinite(inputAnchorPrice) ||
    inputAnchorPrice === 0
  ) {
    return false;
  }
  return Math.abs(predictedOpen - inputAnchorPrice) / Math.abs(inputAnchorPrice) > KRONOS_MAX_OPEN_ANCHOR_GAP_PCT;
}

/**
 * Resolve momentum: is the value real signal or noise?
 *
 * Priority:
 * 1. squeeze_fired → momentum always real (breakout signal)
 * 2. squeeze_on → momentum always real (building energy)
 * 3. squeeze_on=false AND |momentum| >= MOMENTUM_MIN_ABSOLUTE → directional
 * 4. squeeze_on=false AND |momentum| < MOMENTUM_MIN_ABSOLUTE → neutralized
 * 5. squeeze_on=undefined → no data
 */
export function resolveMomentum(data: IntelData): {
  effective: number | undefined;
  neutralized: boolean;
  hasData: boolean;
} {
  const on = data.squeeze_on;
  const fired = data.squeeze_fired;
  const momentum = data.squeeze_momentum;

  if (fired || on) {
    return { effective: momentum, neutralized: false, hasData: true };
  }

  if (on === false) {
    if (momentum !== undefined && momentum !== null && Math.abs(momentum) >= MOMENTUM_MIN_ABSOLUTE) {
      return { effective: momentum, neutralized: false, hasData: true };
    }
    return { effective: 0, neutralized: true, hasData: true };
  }

  return { effective: undefined, neutralized: false, hasData: false };
}

/**
 * Merge volume flow data from two sources:
 * - live snapshot (primary, from getLatestIntelBySymbol)
 * - dedicated VF query (secondary, from getLatestVolumeFlow)
 *
 * Rule: primary wins per-field; secondary fills gaps.
 */
export function mergeVolumeFlow(
  primary: Partial<Pick<IntelData, "obv_divergence_10" | "obv_divergence_20" | "cmf_20" | "mfi_14" | "fbd_flow_score" | "mr_flow_score">>,
  secondary: Partial<Pick<IntelData, "obv_divergence_10" | "obv_divergence_20" | "cmf_20" | "mfi_14" | "fbd_flow_score" | "mr_flow_score">> | null | undefined,
): Pick<IntelData, "obv_divergence_10" | "obv_divergence_20" | "cmf_20" | "mfi_14" | "fbd_flow_score" | "mr_flow_score"> {
  const vfKeys = ["obv_divergence_10", "obv_divergence_20", "cmf_20", "mfi_14", "fbd_flow_score", "mr_flow_score"] as const;
  const result: Record<string, any> = {};
  for (const key of vfKeys) {
    result[key] = primary[key] ?? secondary?.[key] ?? undefined;
  }
  return result as Pick<IntelData, "obv_divergence_10" | "obv_divergence_20" | "cmf_20" | "mfi_14" | "fbd_flow_score" | "mr_flow_score">;
}

/**
 * Full data resolution pipeline.
 *
 * Takes raw IntelData from buildIntelMap and produces ResolvedIntel
 * with all fallback and quality decisions applied.
 *
 * This is the ONLY function App.tsx should call before passing data to translate.ts.
 */
export function resolveIntelData(
  data: IntelData,
  vfSecondary?: Partial<Pick<IntelData, "obv_divergence_10" | "obv_divergence_20" | "cmf_20" | "mfi_14" | "fbd_flow_score" | "mr_flow_score">> | null,
): ResolvedIntel {
  const sessionForecast = resolveSessionForecast(data);
  const kronos4hDisconnected = isKronosProjectionDisconnected(
    data.kronos_4h_predicted_open,
    data.kronos_4h_input_anchor_price,
  );
  const kronosHourlyDisconnected = isKronosProjectionDisconnected(
    data.kronos_hourly_predicted_open,
    data.kronos_hourly_input_anchor_price,
  );
  const momentum = resolveMomentum(data);
  const vf = mergeVolumeFlow(data, vfSecondary);
  const kronos4hUnusable = data.kronos_4h_valid === false || kronos4hDisconnected;
  const kronosHourlyUnusable = data.kronos_hourly_valid === false || kronosHourlyDisconnected;

  const sanitizedForDerivedStance: IntelData = {
    ...data,
    ...(kronos4hUnusable ? { kronos_4h_direction: undefined, kronos_4h_confidence: undefined } : {}),
    ...(kronosHourlyUnusable ? { kronos_hourly_direction: undefined, kronos_hourly_confidence: undefined } : {}),
  };

  // If no intel_stance from live snapshot, derive one from directional layers
  const stance = data.intel_stance ?? deriveIntelStance(sanitizedForDerivedStance);

  return {
    ...data,
    ...(kronos4hUnusable ? { kronos_4h_direction: undefined, kronos_4h_confidence: undefined } : {}),
    ...(kronosHourlyUnusable ? { kronos_hourly_direction: undefined, kronos_hourly_confidence: undefined } : {}),
    // Session forecast resolution
    _session_forecast_dir: sessionForecast.direction,
    _session_forecast_conf: sessionForecast.confidence,
    _session_forecast_source: sessionForecast.source,
    _session_forecast_close: sessionForecast.close,
    _session_forecast_high: sessionForecast.high,
    _session_forecast_low: sessionForecast.low,
    _session_forecast_range: sessionForecast.range,
    // Momentum resolution
    _momentum_neutralized: momentum.neutralized,
    ...(momentum.neutralized ? { squeeze_momentum: 0 } : {}),
    ...(!momentum.hasData ? { squeeze_on: undefined } : {}),
    // Intel stance derivation
    ...(stance && !data.intel_stance ? { intel_stance: stance } : {}),
    // Volume flow merge
    ...vf,
  };
}

// ── Internal helpers ──

/**
 * Derive intel stance from directional layers when no live stance is available.
 *
 * This is the SAME logic that was previously in translate.ts deriveIntelStance(),
 * moved here because stance derivation is a RESOLUTION decision, not a
 * TRANSLATION decision.
 */
function directionLabel(dir?: string): "up" | "down" | "flat" | "unknown" {
  if (!dir) return "unknown";
  const d = dir.toLowerCase();
  if (d.includes("up") || d.includes("bull") || d === "long") return "up";
  if (d.includes("down") || d.includes("bear") || d === "short") return "down";
  if (d.includes("flat") || d.includes("neutral") || d.includes("ranging") || d.includes("range")) return "flat";
  return "unknown";
}

function deriveIntelStance(data: IntelData): string | undefined {
  const signalDir = directionLabel(data.direction);
  if (signalDir === "unknown") return undefined;

  const layerDirs = [
    directionLabel(data.hmm_regime),
    directionLabel(data.hmm_4h_regime),
    directionLabel(data.hmm_hourly_regime),
    directionLabel(data.kronos_direction),
    directionLabel(data.kronos_4h_direction),
    directionLabel(data.kronos_hourly_direction),
  ].filter(dir => dir !== "unknown");

  if (layerDirs.length === 0) return undefined;

  const agree = layerDirs.filter(dir => dir === signalDir).length;
  const against = layerDirs.filter(dir => dir !== "flat" && dir !== signalDir).length;

  if (agree >= 3 && against === 0) return signalDir === "up" ? "aligned_bullish" : "aligned_bearish";
  if (against > agree) return "mixed";
  if (agree > against) return signalDir === "up" ? "aligned_bullish" : "aligned_bearish";
  return "neutral";
}
